import { AspectRatioProps, Box, BoxProps, Container } from '@chakra-ui/react'
import { observer } from 'mobx-react-lite'
import { useStore } from '@store'
import { fadeIn } from '@lib/motion'
import { useInViewMotion } from '@lib/hooks'
import { MotionBox, MotionText, HeroAspectRatio } from '@ui'
import { IFonts } from '@interfaces'

import { useRouter } from 'next/router'

interface IHero {
  subTitle?: string
  title?: string
  description?: string
  bgImageUrl: string
  mobileBgImageUrl?: string
  headerNav?: JSX.Element
  children?: JSX.Element
  /**
   * There is only one photo on hero component.
   */
  customHero?: boolean
  isCoveredBg?: boolean
  aspectRatio?: AspectRatioProps['ratio']
  containerProps?: BoxProps
  activeFontFamily?: IFonts
}

// Tek duraklı 0% → 50% gradient üstte yatay bant/çizgi oluşturur; çok duraklı
// + radial katman ile yumuşak geçiş sağlanır.
const HERO_OVERLAY_GRADIENT = `
  radial-gradient(ellipse 130% 85% at 50% -25%, rgba(13, 16, 29, 0.72) 0%, transparent 62%),
  linear-gradient(
    180deg,
    rgba(13, 16, 29, 0.68) 0%,
    rgba(13, 16, 29, 0.42) 14%,
    rgba(13, 16, 29, 0.22) 30%,
    rgba(13, 16, 29, 0.08) 48%,
    rgba(13, 16, 29, 0.02) 64%,
    rgba(13, 16, 29, 0) 80%
  )
`

const heroOverlayPseudo = {
  content: '""',
  position: 'absolute',
  inset: 0,
  background: HERO_OVERLAY_GRADIENT,
  pointerEvents: 'none',
  zIndex: 1,
} as const

//let thumbimgUrl = imgUrl.replace('full','thumbnail');

const Hero = observer(
  ({
     subTitle,
     title,
     description,
     bgImageUrl,
     mobileBgImageUrl,
     headerNav,
     children,
     customHero,
     isCoveredBg = true,
     aspectRatio,
     containerProps,
     activeFontFamily = 'body',
   }: IHero) => {
    const {
      ui: { displayBannerSlider },
    } = useStore()
    const { controls, ref } = useInViewMotion(true)

    // detect image origin
    const router = useRouter()

    if (bgImageUrl.includes('drupal')) {
      const domain = new URL(bgImageUrl).hostname.replace('www.', '')

      // console.log(domain)

      const isDrupal = domain.includes('drupal')

      // console.log(router.pathname)
      const EaE = router.pathname.includes('exhibitions-and-events')

      if (EaE && isDrupal) {
        let mBgImageUrl = bgImageUrl.replace('styles/webp', 'styles/eae_mobile')

        if (mobileBgImageUrl) {
          //console.log(mobileBgImageUrl)
          mBgImageUrl = mobileBgImageUrl
        }
        // console.log(mobileBgImageUrl)

        const detailNavGradient = headerNav ? heroOverlayPseudo : undefined

        return (
          <HeroAspectRatio>
            <MotionBox
              width="full"
              height="full"
              backgroundImage={{
                sm: `url('${
                  mBgImageUrl === ''
                    ? '/img/visit/hero/ssm-visit.jpg'
                    : mBgImageUrl
                }')`,
                base: `url('${
                  bgImageUrl === ''
                    ? '/img/visit/hero/ssm-visit.jpg'
                    : mBgImageUrl
                }')`,
                lg: `url('${
                  bgImageUrl === ''
                    ? '/img/visit/hero/ssm-visit.jpg'
                    : bgImageUrl
                }')`,
              }}
              //         backgroundImage={`url('${
              //         bgImageUrl === '' ? '/img/visit/hero/ssm-visit.jpg' : bgImageUrl
              //       }')`}
              backgroundPosition="center"
              backgroundSize={isCoveredBg ? 'cover' : 'cover'}
              backgroundRepeat="no-repeat"
              position="relative"
              zIndex={displayBannerSlider ? 5 : 3}
              py={{ base: 'padding.primary', lg: '8' }}
              ref={ref}
              initial="hidden"
              animate={controls}
              variants={fadeIn.animationContainer}
              _before={detailNavGradient}
              sx={
                headerNav
                  ? {
                      '& > *': {
                        position: 'relative',
                        zIndex: 2,
                      },
                    }
                  : undefined
              }
              {...containerProps}
            >
              {headerNav && headerNav}
              <Container
                height={headerNav ? 'auto' : 'full'}
                display="flex"
                flexDirection={{ base: 'column', lg: 'row' }}
                position="relative"
              >
                <Box wordBreak="break-word">
                  {subTitle && (
                    <MotionText
                      fontFamily={activeFontFamily}
                      variants={fadeIn.up}
                      fontSize={{ base: '2xl', lg: '3xl' }}
                      color="red.primary"
                      as="h3"
                      mb={{ base: 1, lg: '0' }}
                    >
                      {subTitle}
                    </MotionText>
                  )}
                  {title && (
                    <MotionText
                      variants={fadeIn.up}
                      fontFamily={activeFontFamily}
                      fontSize={{ base: '3xl', lg: '5xl' }}
                      color="whiteAlpha.primary4"
                      fontWeight="bold"
                      lineHeight="normal"
                      as="h2"
                      mb={{ base: 1, lg: '6px' }}
                    >
                      {title}
                    </MotionText>
                  )}
                  {description && (
                    <MotionText
                      variants={fadeIn.up}
                      fontFamily={activeFontFamily}
                      fontSize="sm"
                      fontWeight="light"
                      color="white.primary"
                      maxW="610px"
                      whiteSpace="break-spaces"
                      noOfLines={{ base: 10, lg: 5 }}
                    >
                      {description}
                    </MotionText>
                  )}
                </Box>
              </Container>
              {children && children}
            </MotionBox>
          </HeroAspectRatio>
        )
      }
    }

    // if domain is drupal and current url is  then change mBgImageURl to EaE style
    // https://drupal.sakipsabancimuzesi.org/sites/muze.sabanciuniv.edu/files/styles/eae_mobile/public/2021-12/Etkinlik-banner-id-16.jpg.webp
    // https://drupal.sakipsabancimuzesi.org/sites/muze.sabanciuniv.edu/files/styles/webp/public/2021-10/Etkinlik-banner-id-4.jpg.webp

    if (customHero) {
      const mobileImage =
        mobileBgImageUrl ||
        (bgImageUrl === '' ? '/img/visit/hero/ssm-visit.jpg' : bgImageUrl)
      const desktopImage =
        bgImageUrl === '' ? '/img/visit/hero/ssm-visit.jpg' : bgImageUrl

      return (
        <HeroAspectRatio ratio={aspectRatio}>
          <Box
            width="full"
            height="full"
            backgroundImage={{
              base: `url('${mobileImage}')`,
              lg: `url('${desktopImage}')`,
            }}
            //   backgroundImage={`url('${
            //   bgImageUrl === '' ? '/img/visit/hero/ssm-visit.jpg' : bgImageUrl
            //  }')`}
            backgroundPosition="center"
            backgroundSize={isCoveredBg ? 'cover' : 'contain'}
            backgroundRepeat="no-repeat"
            position="relative"
            zIndex={displayBannerSlider ? 5 : 3}
            py={{ base: 'padding.primary2', lg: '8' }}
            _before={heroOverlayPseudo}
            {...containerProps}
          />
        </HeroAspectRatio>
      )
    }

    const fallbackImage =
      bgImageUrl === '' ? '/img/visit/hero/ssm-visit.jpg' : bgImageUrl
    const mobileImage = mobileBgImageUrl || fallbackImage

    return (
      <HeroAspectRatio ratio={aspectRatio}>
        <MotionBox
          width="full"
          height="full"
          backgroundImage={{
            base: `url('${mobileImage}')`,
            lg: `url('${fallbackImage}')`,
          }}
          //         backgroundImage={`url('${
          //          bgImageUrl === '' ? '/img/visit/hero/ssm-visit.jpg' : bgImageUrl
          //       }')`}
          backgroundPosition="center"
          backgroundSize={isCoveredBg ? 'cover' : 'contain'}
          backgroundRepeat="no-repeat"
          position="relative"
          zIndex={displayBannerSlider ? 5 : 3}
          py={{ base: 'padding.primary', lg: '8' }}
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={fadeIn.animationContainer}
          _before={heroOverlayPseudo}
          sx={{
            '& > *': {
              position: 'relative',
              zIndex: 2,
            },
          }}
          {...containerProps}
        >
          {headerNav && headerNav}
          <Container
            height={headerNav ? 'auto' : 'full'}
            display="flex"
            flexDirection={{ base: 'column', lg: 'row' }}
            position="relative"
          >
            <Box wordBreak="break-word">
              {subTitle && (
                <MotionText
                  fontFamily={activeFontFamily}
                  variants={fadeIn.up}
                  fontSize={{ base: '2xl', lg: '3xl' }}
                  color="red.primary"
                  as="h3"
                  mb={{ base: 1, lg: '0' }}
                >
                  {subTitle}
                </MotionText>
              )}
              {title && (
                <MotionText
                  variants={fadeIn.up}
                  fontFamily={activeFontFamily}
                  fontSize={{ base: '3xl', lg: '5xl' }}
                  color="whiteAlpha.primary4"
                  opacity={0.8}
                  fontWeight="bold"
                  lineHeight="normal"
                  as="h2"
                  mb={{ base: 1, lg: '6px' }}
                >
                  {title}
                </MotionText>
              )}
              {title && description && (
                <MotionText
                  variants={fadeIn.up}
                  fontFamily={activeFontFamily}
                  fontSize="md"
                  fontWeight="light"
                  color="white.primary"
                  maxW="610px"
                  whiteSpace="break-spaces"
                  noOfLines={{ base: 10, lg: 5 }}
                >
                  {description}
                </MotionText>
              )}
            </Box>
          </Container>
          {children && children}
        </MotionBox>
      </HeroAspectRatio>
    )
  }
)

export default Hero
